import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  TeledeteccionArea,
  CreateAreaRequest,
  UpdateAreaRequest,
  TeledeteccionAreaResponse,
  TeledeteccionAreaListResponse,
  AreaFeature,
  AreaGeometry,
} from '../../../model/teledeteccion-area.model';
import L from 'leaflet';

@Injectable({
  providedIn: 'root',
})
export class TeledeteccionService {
  private url: string;
  private baseEndpoint = 'teledetection'; // Configurable
  creatingArea = signal<AreaFeature | AreaGeometry | null>(null); // GeoJSON puro para el área que se está creando

  // Signals para manejo del estado
  public areas = signal<TeledeteccionArea[]>([]);
  public loading = signal<boolean>(false);
  public error = signal<string | null>(null);

  constructor(private _http: HttpClient) {
    this.url = environment.url_v3;
  }

  /**
   * Establecer el área que se está creando
   */
  setCreatingArea(geojson: AreaFeature | AreaGeometry): void {
    this.creatingArea.set(geojson);
  }

  /**
   * Limpiar el área en creación
   */
  clearCreatingArea(): void {
    this.creatingArea.set(null);
  }

  /**
   * Obtener la URL completa del endpoint
   */
  private getEndpointUrl(path: string): string {
    // Asegurar que el path termine con "/" para que coincida con el backend
    const cleanPath = path.endsWith('/') ? path : path + '/';
    return `${this.url}${this.baseEndpoint}${cleanPath}`;
  }

  /**
   * Crear una nueva área
   */
  createArea(
    areaData: Omit<CreateAreaRequest, 'geojson'>
  ): Observable<TeledeteccionAreaResponse> {
    this.loading.set(true);
    this.error.set(null);

    // Verificar que existe un área en creación
    const currentCreatingArea = this.creatingArea();
    if (!currentCreatingArea) {
      this.loading.set(false);
      const error = new Error(
        'No hay área en creación. Dibuja un polígono primero.'
      );
      this.error.set(error.message);
      throw error;
    }

    // Convertir el objeto a GeoJSON puro para evitar referencias circulares
    let cleanGeojson: AreaFeature | AreaGeometry;
    try {
      // Ya debería ser un GeoJSON puro, pero por seguridad lo validamos
      cleanGeojson = currentCreatingArea;
    } catch (error) {
      console.error('❌ Error al procesar GeoJSON:', error);
      this.loading.set(false);
      throw error;
    }

    // Usar el geojson limpio
    const requestData: CreateAreaRequest = {
      ...areaData,
      geojson: cleanGeojson,
    };

    const url = `${this.url}4pcore/areas`;

    return this._http.post<TeledeteccionAreaResponse>(url, requestData).pipe(
      map(response => {
        this.loading.set(false);
        if (response.area) {
          // Actualizar la lista de áreas añadiendo la nueva
          const currentAreas = this.areas();
          this.areas.set([...currentAreas, response.area]);
          // Limpiar el área en creación
          this.clearCreatingArea();
        } else {
          console.warn('⚠️ Backend no devolvió área en la respuesta');
        }
        return response;
      }),
      catchError(error => {
        this.loading.set(false);
        this.error.set(error.error?.message || 'Error al crear el área');
        throw error;
      })
    );
  }

  /**
   * Obtener un área por ID
   */
  getAreaById(areaId: number): Observable<TeledeteccionAreaResponse> {
    this.loading.set(true);
    this.error.set(null);

    return this._http
      .get<TeledeteccionAreaResponse>(`${this.url}4pcore/areas/${areaId}`)
      .pipe(
        map(response => {
          this.loading.set(false);
          return response;
        }),
        catchError(error => {
          this.loading.set(false);
          this.error.set(error.error?.message || 'Error al obtener el área');
          throw error;
        })
      );
  }

  /**
   * Actualizar un área por ID
   */
  updateArea(
    areaId: number,
    areaData: UpdateAreaRequest
  ): Observable<TeledeteccionAreaResponse> {
    this.loading.set(true);
    this.error.set(null);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http
      .put<TeledeteccionAreaResponse>(
        `${this.url}4pcore/areas/${areaId}`,
        areaData,
        { headers }
      )
      .pipe(
        map(response => {
          this.loading.set(false);
          if (response.area) {
            // Actualizar la lista de áreas
            const currentAreas = this.areas();
            const updatedAreas = currentAreas.map(area =>
              area.id === areaId ? response.area! : area
            );
            this.areas.set(updatedAreas);
          }
          return response;
        }),
        catchError(error => {
          this.loading.set(false);
          this.error.set(error.error?.message || 'Error al actualizar el área');
          throw error;
        })
      );
  }

  /**
   * Eliminar un área por ID
   */
  deleteArea(areaId: number): Observable<TeledeteccionAreaResponse> {
    this.loading.set(true);
    this.error.set(null);

    const url = `${this.url}4pcore/areas/${areaId}`;

    return this._http.delete<TeledeteccionAreaResponse>(url).pipe(
      map(response => {
        this.loading.set(false);
        // Remover el área de la lista
        const currentAreas = this.areas();
        const filteredAreas = currentAreas.filter(area => area.id !== areaId);
        this.areas.set(filteredAreas);
        return response;
      }),
      catchError(error => {
        this.loading.set(false);
        this.error.set(error.error?.message || 'Error al eliminar el área');
        throw error;
      })
    );
  }

  /**
   * Obtener todas las áreas
   */
  getAllAreas(): Observable<TeledeteccionArea[]> {
    this.loading.set(true);
    this.error.set(null);

    const url = this.url + '4pcore/areas';

    return this._http
      .get<TeledeteccionArea[] | TeledeteccionAreaListResponse>(url)
      .pipe(
        map(response => {
          this.loading.set(false);

          let areas: TeledeteccionArea[] = [];

          // Verificar si la respuesta es directamente un array
          if (Array.isArray(response)) {
            areas = response;
          }
          // Verificar si es un objeto con propiedad areas
          else if (
            response &&
            typeof response === 'object' &&
            'areas' in response
          ) {
            areas = (response as TeledeteccionAreaListResponse).areas || [];
          } else {
            console.warn('⚠️ Formato de respuesta no reconocido:', response);
            areas = [];
          }

          this.areas.set(areas);
          return areas;
        }),
        catchError(error => {
          this.loading.set(false);
          this.error.set(error.error?.message || 'Error al obtener las áreas');
          throw error;
        })
      );
  }

  calculateIndex({
    date,
    area_id,
    index_name,
  }: {
    date;
    area_id;
    index_name;
  }): Observable<any> {
    console.log();
    let url = '';
    /* const url = this.getEndpointUrl(`/sentinel/index/`); */
    if (environment.production)
      url = 'https://teledetection.api.4piot.es/sentinel/index/';
    else url = 'https://teledetection.api.dev.4piot.es/sentinel/index/';

    return this._http
      .get<any>(`${url}${index_name}?date=${date}&area_id=${area_id}`, {})
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => {
          this.error.set(error.error?.message || 'Error al calcular el índice');
          throw error;
        })
      );
  }

  /**
   * Obtener estadísticas semanales (mediana, media, etc.) para un índice y área determinados.
   * Endpoint: GET /sentinel/index/{INDEX_NAME}/stats?date=YYYY-MM-DD&area_id=ID
   * Devuelve un rango (por defecto backend: últimos 3 meses hasta date) y un array weekly_stats
   */
  getWeeklyIndexStats({
    date,
    area_id,
    index_name,
  }: {
    date: string;
    area_id: number | string;
    index_name: string;
  }): Observable<any> {
    let base = '';
    if (environment.production)
      base = 'https://teledetection.api.4piot.es/sentinel/index/';
    else base = 'https://teledetection.api.dev.4piot.es/sentinel/index/';

    return this._http
      .get<any>(`${base}${index_name}/stats?date=${date}&area_id=${area_id}`)
      .pipe(
        map(resp => resp),
        catchError(error => {
          this.error.set(
            error.error?.message || 'Error al obtener histórico del índice'
          );
          throw error;
        })
      );
  }

  /**
   * Limpiar el estado de error
   */
  clearError(): void {
    this.error.set(null);
  }

  /**
   * Reiniciar el estado del servicio
   */
  resetState(): void {
    this.areas.set([]);
    this.loading.set(false);
    this.error.set(null);
  }
}
